import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { useResourceList } from '../config/ResourcesConfigContext';
import type { ContentAudience } from '../utils/audience';

export interface HelpDocumentData {
    id: number;
    title: string;
    slug: string;
    /**
     * storage.file id of the PDF in GCS. Null only for a row that failed the
     * GCS migration and has no file attached — the viewer surfaces that as
     * "not available yet" rather than an empty frame.
     */
    pdfFileId: string | null;
    category: string[];
    createdAt: string;
    audience: ContentAudience;
}

interface HelpDocumentContextType {
    helpDocuments: HelpDocumentData[];
    isLoading: boolean;
    error: string | null;
    refresh: (force?: boolean) => Promise<void>;
    reset: () => void;
}

const HelpDocumentContext = createContext<HelpDocumentContextType | undefined>(undefined);

export function HelpDocumentProvider({ children }: { children: ReactNode }) {
    const fetchResource = useResourceList();
    const [helpDocuments, setHelpDocuments] = useState<HelpDocumentData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);

    const fetchHelpDocuments = useCallback(
        async (force: boolean = false) => {
            if (!force && hasFetchedRef.current) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const data = await fetchResource<HelpDocumentData>('helpDocuments');

                if (Array.isArray(data)) {
                    setHelpDocuments(data);
                    hasFetchedRef.current = true;
                } else {
                    console.warn('Invalid help documents data format:', data);
                    setHelpDocuments([]);
                    setError('Unable to load help documents. Please try again later.');
                }
            } catch (err) {
                setError(
                    'Unable to load help documents. Please check your connection and try again.'
                );
                setHelpDocuments([]);
                console.error('Failed to fetch help documents:', err);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchResource]
    );

    const refresh = useCallback(
        async (force: boolean = false) => {
            await fetchHelpDocuments(force);
        },
        [fetchHelpDocuments]
    );
    const reset = useCallback(() => {
        hasFetchedRef.current = false;
        setHelpDocuments([]);
        setError(null);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchHelpDocuments(false);
    }, [fetchHelpDocuments]);

    const value: HelpDocumentContextType = { helpDocuments, isLoading, error, refresh, reset };

    return <HelpDocumentContext.Provider value={value}>{children}</HelpDocumentContext.Provider>;
}

export function useHelpDocuments(): HelpDocumentContextType {
    const context = useContext(HelpDocumentContext);
    if (context === undefined) {
        throw new Error('useHelpDocuments must be used within a HelpDocumentProvider');
    }
    return context;
}
